import { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence } from 'motion/react';
import type { Museum, MuseumDataset } from './data/types';
import { trackPage } from './utils/analytics';
import { AnalyticsConsent } from './components/AnalyticsConsent';
import { BuurtScherm } from './components/BuurtScherm';
import { Footer } from './components/Footer';
import { KaartHerinnering } from './components/KaartHerinnering';
import { FILTERS, Kop, TABS, type Filter, type Tab } from './components/Kop';
import { MenuExtra } from './components/MenuExtra';
import { MotionRoot } from './components/MotionRoot';
import { MuseumPaneel } from './components/MuseumPaneel';
import { OfflineBanner } from './components/OfflineBanner';
import { SettingsMenu } from './components/SettingsMenu';
import { ToastHost } from './components/ToastHost';
import { UpdateToast } from './components/UpdateToast';
import { VoortgangScherm } from './components/VoortgangScherm';
// Niet lazy: bij het eerste gebruik is dit het eerste scherm, dus het moet direct kunnen verschijnen.
import { WelkomModal } from './components/WelkomModal';
import type { VliegNaar } from './components/MuseumMap';
import { UpdateProvider } from './hooks/useAppUpdate';
import { useDonker } from './hooks/useDonker';
import { useLocatie } from './hooks/useLocatie';
import { useMuseumStatus } from './hooks/useMuseumStatus';
import { usePersistentState } from './hooks/usePersistentState';
import { LanguageProvider, useT } from './hooks/useT';
import { useSettings, type Settings } from './hooks/useSettings';
import { useShareLink } from './hooks/useShareLink';
import { useToast } from './hooks/useToast';
import { berekenBadges, berekenVoortgang, isBezocht, vandaag } from './utils/museum';
import { voegSamenStatus, type Terugzetten } from './utils/backup';
import { downloadBestand } from './utils/download';
import { kaartStaat, maakIcs } from './utils/kaart';
import { buildShareUrl, shareOrCopy } from './utils/share';
import { oneOf } from './utils/storage';
import { maakWensValidator, type WensShare } from './utils/wensShare';

// Zware onderdelen pas laden als ze nodig zijn: de kaartbibliotheek en dialogen.
// De kaart wordt tijdens de uitleg alvast gedownload (laadKaart), maar pas opgebouwd na "Naar de kaart".
const laadKaart = () => import('./components/MuseumMap');
const MuseumMap = lazy(laadKaart);
const ImportModal = lazy(() => import('./components/ImportModal').then((m) => ({ default: m.ImportModal })));
const SharePreviewModal = lazy(() => import('./components/SharePreviewModal').then((m) => ({ default: m.SharePreviewModal })));

const GEEN_MUSEA: Museum[] = [];

/**
 * De museumlijst wordt los geladen, zodat de app eerst zijn kop en knoppen toont.
 * De kaartbibliotheek start pas daarna, als de browser even rust heeft.
 */
function useMuseumData(kaartToegestaan: boolean) {
  const [dataset, setDataset] = useState<MuseumDataset | null>(null);
  const [kaartStart, setKaartStart] = useState(false);
  useEffect(() => {
    let actief = true;
    import('./data/museums.json').then((m) => actief && setDataset(m.default as MuseumDataset));
    const start = () => actief && setKaartStart(true);
    // Niet elke browser kent requestIdleCallback (o.a. Safari): dan een korte timeout.
    const idle = typeof window.requestIdleCallback === 'function';
    const id = idle ? window.requestIdleCallback(start, { timeout: 1500 }) : setTimeout(start, 300);
    return () => {
      actief = false;
      if (idle) window.cancelIdleCallback(id as number);
      else clearTimeout(id);
    };
  }, []);
  const musea = dataset?.musea ?? GEEN_MUSEA;
  const perId = useMemo<ReadonlyMap<string, Museum>>(() => new Map(musea.map((m) => [m.id, m])), [musea]);
  const valideerWensen = useMemo(() => (dataset ? maakWensValidator(new Set(perId.keys())) : null), [dataset, perId]);
  // Tijdens de uitleg alleen downloaden: dat kost nauwelijks rekenwerk.
  useEffect(() => {
    if (kaartStart && !kaartToegestaan) laadKaart().catch(() => undefined);
  }, [kaartStart, kaartToegestaan]);
  return { dataset, musea, perId, valideerWensen, kaartStart: kaartStart && kaartToegestaan && dataset !== null };
}

export default function App() {
  const settings = useSettings();
  return (
    <MotionRoot>
      <LanguageProvider language={settings.language}>
        <UpdateProvider>
          <Scherm settings={settings} />
        </UpdateProvider>
      </LanguageProvider>
    </MotionRoot>
  );
}

function Scherm({ settings }: { settings: Settings }) {
  const { t, tf } = useT();
  const {
    status,
    setStatus,
    kaart,
    setKaart,
    wissel,
    extraBezoek,
    zetOpWensenlijst,
    laatsteBackup,
    setLaatsteBackup,
    herinneringGezien,
    setHerinneringGezien,
  } = useMuseumStatus();
  const locatie = useLocatie();
  const donker = useDonker();
  const { toast, showToast, dismissToast } = useToast();
  const [welkomGezien, setWelkomGezien] = usePersistentState<boolean>('mk-welkom-v1', false, (r) => (typeof r === 'boolean' ? r : undefined));
  const [uitlegOpen, setUitlegOpen] = useState(false);
  const { dataset, musea: MUSEA, perId: PER_ID, valideerWensen, kaartStart } = useMuseumData(welkomGezien);
  const share = useShareLink<WensShare>(valideerWensen);

  const [tab, setTab] = usePersistentState<Tab>('mk-tab', 'kaart', oneOf(TABS));
  // Geen router: elke tabwissel is een eigen weergave in Google Analytics (alleen na toestemming).
  useEffect(() => {
    const titel = { kaart: t('tabKaart'), buurt: t('tabBuurt'), voortgang: t('tabVoortgang') }[tab];
    trackPage(`/${tab}`, `${titel} – ${document.title}`);
  }, [tab, t]);
  const [filter, setFilter] = usePersistentState<Filter>('mk-filter', 'alles', oneOf(FILTERS));
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [vliegNaar, setVliegNaar] = useState<VliegNaar | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  // Hoogte van de zwevende kop, zodat kaart en pagina's eronder beginnen.
  const kopRef = useRef<HTMLDivElement>(null);
  const [kopHoogte, setKopHoogte] = useState(200);
  useLayoutEffect(() => {
    const el = kopRef.current;
    if (!el) return;
    const meet = () => setKopHoogte(el.offsetHeight);
    meet();
    const ro = new ResizeObserver(meet);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (share.invalid) showToast(t('shareInvalidToast'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [share.invalid]);

  const zichtbaar = useMemo(() => {
    if (filter === 'alles') return MUSEA;
    return MUSEA.filter((m) => {
      const s = status[m.id];
      if (filter === 'niet') return !isBezocht(s);
      if (filter === 'bezocht') return isBezocht(s);
      return !!s?.[filter === 'wens' ? 'w' : 'f'];
    });
  }, [filter, status, MUSEA]);

  const voortgang = useMemo(() => berekenVoortgang(MUSEA, status, kaart), [MUSEA, status, kaart]);
  const badges = useMemo(() => berekenBadges(MUSEA, status, voortgang, kaart), [MUSEA, status, voortgang, kaart]);
  const geselecteerd = selectedId ? PER_ID.get(selectedId) : undefined;
  const kentIds = useMemo<ReadonlySet<string>>(() => new Set(PER_ID.keys()), [PER_ID]);
  const staat = useMemo(() => kaartStaat(kaart, vandaag()), [kaart]);
  const wensNog = useMemo(() => MUSEA.filter((m) => status[m.id]?.w && !isBezocht(status[m.id])).length, [MUSEA, status]);
  const toonHerinnering =
    welkomGezien &&
    tab === 'kaart' &&
    (staat.soort === 'verlopen' || (staat.soort === 'geldig' && staat.binnenkort)) &&
    herinneringGezien !== staat.verval;

  const zetAgendaBestand = () => {
    if (staat.soort !== 'geldig') return;
    const ok = downloadBestand(
      'museumkaart-herinnering.ics',
      maakIcs(staat.verval, { herinnering: t('kaartAgendaTitel'), verlopen: t('kaartAgendaVerlopen'), uitleg: t('kaartAgendaUitleg') }),
      'text/calendar',
    );
    showToast(ok ? t('kaartAgendaOk') : t('shareFailedToast'));
  };

  const pasTerugzettenToe = (gevonden: Terugzetten, modus: 'samenvoegen' | 'overschrijven') => {
    const oudeStatus = status;
    const oudeKaart = kaart;
    setStatus(modus === 'samenvoegen' ? voegSamenStatus(status, gevonden.status) : gevonden.status);
    if (gevonden.kaart) {
      // Samenvoegen houdt je eigen ingangsdatum, tenzij je die nog niet had ingevuld.
      setKaart(modus === 'samenvoegen' ? { prijs: gevonden.kaart.prijs, vanaf: kaart.vanaf ?? gevonden.kaart.vanaf } : gevonden.kaart);
    }
    setImportOpen(false);
    setMenuOpen(false);
    showToast(t('importKlaar'), 7000, {
      label: t('undo'),
      onClick: () => {
        setStatus(oudeStatus);
        setKaart(oudeKaart);
      },
    });
  };

  const kies = useCallback(
    (m: Museum) => {
      setTab('kaart');
      setSelectedId(m.id);
      setVliegNaar({ lat: m.lat, lng: m.lng, key: Date.now() });
    },
    [setTab],
  );

  const onWissel = (m: Museum, wat: 'b' | 'w' | 'f') => {
    const s = status[m.id];
    const wasAan = wat === 'b' ? isBezocht(s) : !!s?.[wat];
    const herstel = wissel(m.id, wat);
    const tekst =
      wat === 'b'
        ? tf(wasAan ? 'onbezochtToast' : 'bezochtToast', { naam: m.naam })
        : t(wat === 'w' ? (wasAan ? 'wensUitToast' : 'wensAanToast') : wasAan ? 'favUitToast' : 'favAanToast');
    showToast(tekst, 5000, { label: t('undo'), onClick: herstel });
  };

  const naarMijnLocatie = () =>
    locatie.vraag((pos) => setVliegNaar({ ...pos, zoom: 11, key: Date.now() }));
  useEffect(() => {
    if (locatie.status === 'fout' && tab === 'kaart') showToast(t('buurtGeweigerd'), 5000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locatie.status]);

  const deelWensenlijst = async () => {
    const ids = MUSEA.filter((m) => status[m.id]?.w).map((m) => m.id);
    if (!ids.length) {
      showToast(t('wensLeeg'), 4000);
      return;
    }
    const result = await shareOrCopy({
      title: t('metaTitle'),
      text: tf('shareText', { n: ids.length }),
      url: buildShareUrl({ w: ids }),
    });
    if (result === 'copied') showToast(t('shareCopiedToast'));
    if (result === 'failed') showToast(t('shareFailedToast'));
  };

  const neemWensenOver = (p: WensShare) => {
    const herstel = zetOpWensenlijst(p.w);
    share.dismiss();
    setFilter('wens');
    setTab('kaart');
    showToast(tf('shareAppliedToast', { n: p.w.length }), 6000, { label: t('undo'), onClick: herstel });
  };

  const kopieer = async (tekst: string) => {
    try {
      await navigator.clipboard.writeText(tekst);
      showToast(t('adresGekopieerd'));
    } catch {
      showToast(t('shareFailedToast'));
    }
  };

  return (
    <div className="relative h-dvh overflow-hidden">
      <OfflineBanner />
      <AnalyticsConsent verberg={!welkomGezien || uitlegOpen || selectedId !== null || menuOpen} />
      <UpdateToast />
      <ToastHost toast={toast} onDismiss={dismissToast} />

      <Kop
        ref={kopRef}
        tab={tab}
        setTab={setTab}
        filter={filter}
        setFilter={setFilter}
        musea={MUSEA}
        status={status}
        aantalZichtbaar={zichtbaar.length}
        onKies={kies}
        onMenu={() => setMenuOpen(true)}
      />

      {/* De kaart blijft altijd geladen (geen herstart bij tabwissel); op andere tabs is hij verborgen. */}
      <div className={tab === 'kaart' ? '' : 'invisible'} aria-hidden={tab !== 'kaart'}>
        {!kaartStart && <p className="absolute inset-0 grid place-items-center text-slate-600 dark:text-slate-400">{t('kaartLaden')}</p>}
        {kaartStart && (
          <Suspense fallback={<p className="absolute inset-0 grid place-items-center text-slate-600 dark:text-slate-400">{t('kaartLaden')}</p>}>
            <MuseumMap
              musea={zichtbaar}
              status={status}
              selectedId={selectedId}
              onSelect={setSelectedId}
              dark={donker}
              vliegNaar={vliegNaar}
              mijnPositie={locatie.pos}
              onMijnLocatie={naarMijnLocatie}
              bovenMarge={kopHoogte}
            />
          </Suspense>
        )}
      </div>

      {tab !== 'kaart' && (
        <div className="absolute inset-0 z-10 overflow-y-auto" style={{ paddingTop: kopHoogte + 8 }}>
          <main className="mx-auto w-full max-w-xl space-y-6 p-4 pb-24 sm:p-6">
            {tab === 'buurt' ? (
              <BuurtScherm musea={MUSEA} status={status} locatie={locatie} onVraagLocatie={() => locatie.vraag()} onKies={kies} />
            ) : (
              <VoortgangScherm
                voortgang={voortgang}
                badges={badges}
                kaart={kaart}
                staat={staat}
                wensNog={wensNog}
                onDeel={deelWensenlijst}
                onAgenda={zetAgendaBestand}
                onKaartgegevens={() => setMenuOpen(true)}
              />
            )}
          </main>
          <Footer />
        </div>
      )}

      {toonHerinnering && (staat.soort === 'geldig' || staat.soort === 'verlopen') && (
        <KaartHerinnering
          staat={staat}
          bovenMarge={kopHoogte}
          onBekijk={() => setTab('voortgang')}
          onSluit={() => setHerinneringGezien(staat.verval)}
        />
      )}

      <AnimatePresence>
        {tab === 'kaart' && geselecteerd && (
          <MuseumPaneel
            key="paneel"
            museum={geselecteerd}
            status={status[geselecteerd.id]}
            mijnPositie={locatie.pos}
            onWissel={(wat) => onWissel(geselecteerd, wat)}
            onExtraBezoek={() => {
              extraBezoek(geselecteerd.id);
              showToast(t('bezoekToegevoegd'));
            }}
            onKopieer={kopieer}
            onClose={() => setSelectedId(null)}
          />
        )}
      </AnimatePresence>

      {menuOpen && (
        <SettingsMenu settings={settings} onClose={() => setMenuOpen(false)} onShare={deelWensenlijst}>
          {dataset && (
            <MenuExtra
              kaart={kaart}
              setKaart={setKaart}
              dataset={dataset}
              musea={MUSEA}
              status={status}
              laatsteBackup={laatsteBackup}
              onBackupGemaakt={setLaatsteBackup}
              onMelding={(tekst) => showToast(tekst)}
              onHerstel={() => setImportOpen(true)}
              onUitleg={() => {
                setMenuOpen(false);
                setUitlegOpen(true);
              }}
            />
          )}
        </SettingsMenu>
      )}
      {(!welkomGezien || uitlegOpen) && (
        <WelkomModal
          aantal={dataset ? MUSEA.length : null}
          onClose={() => {
            setWelkomGezien(true);
            setUitlegOpen(false);
          }}
        />
      )}
      {importOpen && (
        <Suspense fallback={null}>
          <ImportModal kent={kentIds} onToepassen={pasTerugzettenToe} onClose={() => setImportOpen(false)} />
        </Suspense>
      )}
      {share.pending && welkomGezien && (
        <Suspense fallback={null}>
          <SharePreviewModal payload={share.pending} musea={PER_ID} onAccept={() => neemWensenOver(share.pending!)} onDismiss={share.dismiss} />
        </Suspense>
      )}
    </div>
  );
}
